/**
 * Single money formatter for the pharmacy module.
 *
 * It previously used three different approaches - `formatToINR` (no decimals),
 * a bare `.toFixed(2)`, and a couple of local Intl formatters - so the same
 * amount rendered as "₹ 30", "30.00" or "₹30.00" depending on the screen.
 * Everything on display goes through here: always two decimals, Indian digit
 * grouping.
 */
const AMOUNT_FORMAT = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

/** 1234.5 -> "1,234.50". Use where the column header already says the unit. */
export const formatAmount = (value, fallback = "-") => {
  const amount = toNumber(value);
  return amount === null ? fallback : AMOUNT_FORMAT.format(amount);
};

/** 1234.5 -> "₹1,234.50". */
export const formatMoney = (value, fallback = "-") => {
  const amount = toNumber(value);
  return amount === null ? fallback : `₹${AMOUNT_FORMAT.format(amount)}`;
};

const RUPEE_FORMAT = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

/**
 * 1234.5 -> "₹ 1,234.5"; 1234 -> "₹ 1,234".
 *
 * The approved Sale Entry design writes money this way - a space after the
 * symbol, and paise only when there are any - and that screen is read as a
 * column of figures where the trailing ".00" on every line is noise. Everywhere
 * else still uses formatAmount/formatMoney: two decimals, always.
 */
export const formatRupees = (value, fallback = "-") => {
  const amount = toNumber(value);
  if (amount === null) return fallback;
  const whole = Math.abs(amount % 1) < 0.005;
  return `₹ ${whole ? RUPEE_FORMAT.format(Math.round(amount)) : AMOUNT_FORMAT.format(amount)}`;
};

/** 1234.5 -> "₹0.00" rather than a dash, for totals that should read as zero. */
export const formatMoneyOrZero = (value) => formatMoney(value ?? 0, "₹0.00");

export default formatMoney;
